/**
 * Shared utilities for the ops console. Keeps error handling and constants in one place.
 */

/** Timezone options for run add/edit (admin can leave empty to derive from address). */
export const CONSOLE_TIMEZONE_OPTIONS = (() => {
  const empty = { value: "", label: "— Leave empty (derive from address)" };
  try {
    if (typeof Intl !== "undefined" && Intl.supportedValuesOf) {
      const zones = Intl.supportedValuesOf("timeZone").sort();
      return [empty, ...zones.map((z) => ({ value: z, label: z }))];
    }
  } catch (_) {}
  const common = [
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix",
    "America/Toronto", "America/Vancouver", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
    "Asia/Tokyo", "Asia/Shanghai", "Australia/Sydney", "Pacific/Auckland", "UTC",
  ];
  return [empty, ...common.sort().map((z) => ({ value: z, label: z }))];
})();

/** Editable external URLs on a club (console detail panel + admin PATCH). */
export const CLUB_EDITABLE_LINK_FIELDS = {
  website: { label: "Website", maxLen: 500 },
  instagram: { label: "Instagram", maxLen: 200 },
  strava_url: { label: "Strava", maxLen: 500 },
  podcast_url: { label: "Podcast", maxLen: 500 },
  community_url: { label: "Community", maxLen: 500 },
};

/** @param {string} field */
export function isClubEditableLinkField(field) {
  return typeof field === "string" && Object.prototype.hasOwnProperty.call(CLUB_EDITABLE_LINK_FIELDS, field);
}

/**
 * Link fields stored on the club besides `website` (website is onboarded separately in payloads).
 * Keeps INITIAL_ONBOARD_FORM / buildOnboardPayload in sync with CLUB_EDITABLE_LINK_FIELDS.
 * @returns {string[]}
 */
export function getClubAuxiliaryLinkFieldKeys() {
  return Object.keys(CLUB_EDITABLE_LINK_FIELDS).filter((k) => k !== "website");
}

/** Safe href for user-supplied URLs: http(s) only, or bare host → https (console links). */
export function safeExternalHref(url) {
  if (url == null || typeof url !== "string") return null;
  const u = url.trim();
  if (!u) return null;
  const lower = u.toLowerCase();
  if (lower.startsWith("http://") || lower.startsWith("https://")) return u;
  if (!/^\w+:/.test(u) && /^[a-zA-Z0-9][\w.-]*(\/.*)?$/.test(u)) return `https://${u}`;
  return null;
}

/** Base for static markdown served from `frontend/public/docs/` (CRA `PUBLIC_URL` prefix when set). */
const CONSOLE_PUBLIC_DOCS_BASE = `${process.env.PUBLIC_URL || ""}/docs`;

/** Canonical city naming, UUID `?city=` flow, timezone tips (mirror: `docs/console-international-onboarding.md`). */
export const CONSOLE_INTL_ONBOARDING_DOC_URL = `${CONSOLE_PUBLIC_DOCS_BASE}/console-international-onboarding.md`;
/** City onboarding SOP (mirror: `docs/city-onboarding-sop.md`). */
export const CONSOLE_CITY_ONBOARDING_SOP_DOC_URL = `${CONSOLE_PUBLIC_DOCS_BASE}/city-onboarding-sop.md`;
/** City onboarding checklist (mirror: `docs/city-onboarding-checklist.md`). */
export const CONSOLE_CITY_ONBOARDING_CHECKLIST_DOC_URL = `${CONSOLE_PUBLIC_DOCS_BASE}/city-onboarding-checklist.md`;

/**
 * Display name for the current admin user (feedback created_by, note author).
 * @param {{ name?: string, email?: string } | null} user - Auth user
 * @returns {string}
 */
export function getAdminDisplayName(user) {
  if (!user) return "Admin";
  const name = user.name && typeof user.name === "string" ? user.name.trim() : "";
  if (name) return name;
  const email = user.email && typeof user.email === "string" ? user.email.trim() : "";
  return email || "Admin";
}

/**
 * Format an ISO date string for console display (short date + short time).
 * @param {string} [iso] - ISO date string
 * @returns {string}
 */
export function formatConsoleDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString(undefined, { dateStyle: "short" }) +
      " " +
      d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true })
    );
  } catch {
    return String(iso);
  }
}

/**
 * Returns the current city filter from URL search params (single source of truth for console city filter).
 * @param {URLSearchParams} searchParams - from useSearchParams()
 * @returns {string|undefined} city id/slug or undefined if not set
 */
export function getCityFromSearchParams(searchParams) {
  const value = searchParams.get("city");
  return value || undefined;
}

/**
 * Build console URL for a city tab (overview, coverage, runs, clubs, etc.). Single place for route + city param.
 * @param {string} tab - path segment after /console/ (e.g. "overview", "coverage")
 * @param {string} cityId - city id or slug
 * @returns {string}
 */
export function getConsoleCityUrl(tab, cityId) {
  if (!cityId) return `/console/${tab}`;
  return `/console/${tab}?city=${encodeURIComponent(cityId)}`;
}

/** Date-range filter options for network overview and coverage (must match backend NETWORK_OVERVIEW_RANGES). */
export const CONSOLE_RANGE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "next_7_days", label: "Next 7 days" },
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "this_week", label: "This week" },
  { value: "next_week", label: "Next week" },
];

/** Default range when none selected (must match backend default). */
export const CONSOLE_DEFAULT_RANGE = "next_7_days";

/** Query param for meetings/coverage window (layout bar, overview, coverage, runs). */
export const CONSOLE_MEETINGS_RANGE_PARAM = "meetings_range";

const CONSOLE_RANGE_VALUE_SET = new Set(CONSOLE_RANGE_OPTIONS.map((o) => o.value));

/**
 * @param {URLSearchParams} searchParams
 * @returns {string}
 */
export function getConsoleMeetingsRangeFromSearchParams(searchParams) {
  const raw = (searchParams.get(CONSOLE_MEETINGS_RANGE_PARAM) || "").trim();
  if (raw && CONSOLE_RANGE_VALUE_SET.has(raw)) return raw;
  return CONSOLE_DEFAULT_RANGE;
}

/**
 * @param {URLSearchParams} searchParams
 * @param {string} range
 * @returns {URLSearchParams}
 */
export function setConsoleMeetingsRangeOnSearchParams(searchParams, range) {
  const next = new URLSearchParams(searchParams);
  const v = CONSOLE_RANGE_VALUE_SET.has(range) ? range : CONSOLE_DEFAULT_RANGE;
  if (v === CONSOLE_DEFAULT_RANGE) {
    next.delete(CONSOLE_MEETINGS_RANGE_PARAM);
  } else {
    next.set(CONSOLE_MEETINGS_RANGE_PARAM, v);
  }
  return next;
}

/**
 * City tab links: preserve `city` and optional non-default meetings range.
 * @param {string} path - e.g. "overview"
 * @param {string|null|undefined} cityId
 * @param {string} meetingsRange
 * @returns {string}
 */
export function getConsoleCityTabPath(path, cityId, meetingsRange) {
  if (!cityId) return `/console/${path}`;
  const next = new URLSearchParams();
  next.set("city", cityId);
  const r = CONSOLE_RANGE_VALUE_SET.has(meetingsRange) ? meetingsRange : CONSOLE_DEFAULT_RANGE;
  if (r !== CONSOLE_DEFAULT_RANGE) {
    next.set(CONSOLE_MEETINGS_RANGE_PARAM, r);
  }
  return `/console/${path}?${next.toString()}`;
}

/**
 * Build console URL for a specific club detail page. Preserves city and optional schedule window.
 * @param {string} cityId - city id or slug (optional)
 * @param {string} clubId - club id
 * @param {string} [meetingsRange] - when set and not default, adds meetings_range query param
 * @returns {string}
 */
export function getConsoleClubUrl(cityId, clubId, meetingsRange) {
  if (!clubId) return getConsoleCityTabPath("clubs", cityId, meetingsRange ?? CONSOLE_DEFAULT_RANGE);
  const base = `/console/clubs/${encodeURIComponent(clubId)}`;
  if (!cityId) {
    if (meetingsRange && CONSOLE_RANGE_VALUE_SET.has(meetingsRange) && meetingsRange !== CONSOLE_DEFAULT_RANGE) {
      return `${base}?${CONSOLE_MEETINGS_RANGE_PARAM}=${encodeURIComponent(meetingsRange)}`;
    }
    return base;
  }
  const next = new URLSearchParams();
  next.set("city", cityId);
  const r = CONSOLE_RANGE_VALUE_SET.has(meetingsRange) ? meetingsRange : CONSOLE_DEFAULT_RANGE;
  if (r !== CONSOLE_DEFAULT_RANGE) {
    next.set(CONSOLE_MEETINGS_RANGE_PARAM, r);
  }
  return `${base}?${next.toString()}`;
}

/**
 * Build console URL for a specific run detail page. Preserves city and optional schedule window.
 * @param {string} cityId - city id or slug (optional)
 * @param {string} runId - run (meeting) id
 * @param {string} [meetingsRange] - when set and not default, adds meetings_range query param
 * @returns {string}
 */
export function getConsoleRunUrl(cityId, runId, meetingsRange) {
  if (!runId) return getConsoleCityTabPath("runs", cityId, meetingsRange ?? CONSOLE_DEFAULT_RANGE);
  const base = `/console/runs/${encodeURIComponent(runId)}`;
  if (!cityId) {
    if (meetingsRange && CONSOLE_RANGE_VALUE_SET.has(meetingsRange) && meetingsRange !== CONSOLE_DEFAULT_RANGE) {
      return `${base}?${CONSOLE_MEETINGS_RANGE_PARAM}=${encodeURIComponent(meetingsRange)}`;
    }
    return base;
  }
  const next = new URLSearchParams();
  next.set("city", cityId);
  const r = CONSOLE_RANGE_VALUE_SET.has(meetingsRange) ? meetingsRange : CONSOLE_DEFAULT_RANGE;
  if (r !== CONSOLE_DEFAULT_RANGE) {
    next.set(CONSOLE_MEETINGS_RANGE_PARAM, r);
  }
  return `${base}?${next.toString()}`;
}

/**
 * Human label for a console schedule window value.
 * @param {string} rangeKey
 * @returns {string}
 */
export function getConsoleRangeLabel(rangeKey) {
  const opt = CONSOLE_RANGE_OPTIONS.find((o) => o.value === rangeKey);
  return opt ? opt.label : CONSOLE_RANGE_OPTIONS.find((o) => o.value === CONSOLE_DEFAULT_RANGE).label;
}

/**
 * UTC bounds for a schedule window, mirroring backend `_network_overview_date_range`.
 * @param {string} rangeKey
 * @returns {{ startMs: number, endMs: number } | null} null for "all"
 */
export function getConsoleNetworkOverviewBoundsMs(rangeKey) {
  const key = CONSOLE_RANGE_VALUE_SET.has(rangeKey) ? rangeKey : CONSOLE_DEFAULT_RANGE;
  if (key === "all") return null;

  const now = new Date();

  if (key === "next_7_days") {
    const startMs = now.getTime();
    const endMs = startMs + 7 * 24 * 60 * 60 * 1000;
    return { startMs, endMs };
  }

  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const d = now.getUTCDate();
  const utcDay = now.getUTCDay();
  const pythonWeekday = (utcDay + 6) % 7;

  if (key === "today") {
    return {
      startMs: Date.UTC(y, m, d, 0, 0, 0, 0),
      endMs: Date.UTC(y, m, d, 23, 59, 59, 999),
    };
  }

  if (key === "tomorrow") {
    const t = new Date(Date.UTC(y, m, d));
    t.setUTCDate(t.getUTCDate() + 1);
    const ty = t.getUTCFullYear();
    const tm = t.getUTCMonth();
    const td = t.getUTCDate();
    return {
      startMs: Date.UTC(ty, tm, td, 0, 0, 0, 0),
      endMs: Date.UTC(ty, tm, td, 23, 59, 59, 999),
    };
  }

  if (key === "this_week") {
    const mondayDate = new Date(Date.UTC(y, m, d));
    mondayDate.setUTCDate(mondayDate.getUTCDate() - pythonWeekday);
    const my = mondayDate.getUTCFullYear();
    const mm = mondayDate.getUTCMonth();
    const md = mondayDate.getUTCDate();
    const sundayDate = new Date(Date.UTC(my, mm, md));
    sundayDate.setUTCDate(sundayDate.getUTCDate() + 6);
    const sy = sundayDate.getUTCFullYear();
    const sm = sundayDate.getUTCMonth();
    const sd = sundayDate.getUTCDate();
    return {
      startMs: Date.UTC(my, mm, md, 0, 0, 0, 0),
      endMs: Date.UTC(sy, sm, sd, 23, 59, 59, 999),
    };
  }

  if (key === "next_week") {
    const mondayThis = new Date(Date.UTC(y, m, d));
    mondayThis.setUTCDate(mondayThis.getUTCDate() - pythonWeekday);
    const mondayNext = new Date(
      Date.UTC(mondayThis.getUTCFullYear(), mondayThis.getUTCMonth(), mondayThis.getUTCDate())
    );
    mondayNext.setUTCDate(mondayNext.getUTCDate() + 7);
    const my = mondayNext.getUTCFullYear();
    const mm = mondayNext.getUTCMonth();
    const md = mondayNext.getUTCDate();
    const sundayNext = new Date(Date.UTC(my, mm, md));
    sundayNext.setUTCDate(sundayNext.getUTCDate() + 6);
    const sy = sundayNext.getUTCFullYear();
    const sm = sundayNext.getUTCMonth();
    const sd = sundayNext.getUTCDate();
    return {
      startMs: Date.UTC(my, mm, md, 0, 0, 0, 0),
      endMs: Date.UTC(sy, sm, sd, 23, 59, 59, 999),
    };
  }

  const startMs = now.getTime();
  const endMs = startMs + 7 * 24 * 60 * 60 * 1000;
  return { startMs, endMs };
}

/**
 * @param {{ scheduled_date?: string } | null} run
 * @param {string} rangeKey
 * @returns {boolean}
 */
export function isRunInConsoleScheduleWindow(run, rangeKey) {
  if (!run?.scheduled_date) return false;
  if (rangeKey === "all") return true;
  const bounds = getConsoleNetworkOverviewBoundsMs(rangeKey);
  if (!bounds) return true;
  const t = new Date(run.scheduled_date).getTime();
  if (Number.isNaN(t)) return false;
  return t >= bounds.startMs && t <= bounds.endMs;
}

/**
 * Run is in the schedule window and scheduled at or after `now`.
 * @param {{ scheduled_date?: string } | null} run
 * @param {Date} now
 * @param {string} rangeKey
 * @returns {boolean}
 */
export function isRunUpcomingInScheduleWindow(run, now, rangeKey) {
  if (!run?.scheduled_date) return false;
  const t = new Date(run.scheduled_date).getTime();
  if (Number.isNaN(t)) return false;
  if (t < now.getTime()) return false;
  return isRunInConsoleScheduleWindow(run, rangeKey);
}

/**
 * Run is in the schedule window and scheduled at or before `now`.
 * @param {{ scheduled_date?: string } | null} run
 * @param {Date} now
 * @param {string} rangeKey
 * @returns {boolean}
 */
export function isRunCompletedInScheduleWindow(run, now, rangeKey) {
  if (!run?.scheduled_date) return false;
  const t = new Date(run.scheduled_date).getTime();
  if (Number.isNaN(t)) return false;
  if (t > now.getTime()) return false;
  return isRunInConsoleScheduleWindow(run, rangeKey);
}

/**
 * Short subtitle for modals: UTC range for the selected window (not for "all").
 * @param {string} rangeKey
 * @returns {string}
 */
export function formatConsoleScheduleWindowSubtitleUtc(rangeKey) {
  if (rangeKey === "all") {
    return "All dates — counts split future vs past among loaded runs.";
  }
  const b = getConsoleNetworkOverviewBoundsMs(rangeKey);
  if (!b) return "";
  const dtf = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  });
  return `${dtf.format(new Date(b.startMs))} → ${dtf.format(new Date(b.endMs))} UTC`;
}

/**
 * Empty-state line for club run lists modals.
 * @param {string} rangeKey
 * @param {"upcoming"|"completed"} mode
 * @returns {string}
 */
export function getConsoleScheduleWindowRunsEmptyMessage(rangeKey, mode) {
  if (rangeKey === "all") {
    return mode === "completed" ? "No completed runs among loaded runs." : "No upcoming runs among loaded runs.";
  }
  const label = getConsoleRangeLabel(rangeKey).toLowerCase();
  return mode === "completed" ? `No completed runs in ${label}.` : `No upcoming runs in ${label}.`;
}

/** Day labels for coverage/slot grids (order: Mon–Sun). */
export const CONSOLE_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Full day names for City Rhythm and labels. */
export const CONSOLE_DAY_FULL = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

/** Slot labels. */
export const CONSOLE_SLOTS = ["am", "pm"];

/**
 * South Florida metro sub-regions (same grouping as reference console).
 * Used when the selected city is South Florida so clubs/runs can be filtered and grouped by region.
 */
export const SOUTH_FLORIDA_REGIONS = [
  "Miami",
  "Miami Beach",
  "Coral Gables / Coconut Grove",
  "North Miami / North Bay Village",
  "Doral / Kendall / West Kendall",
  "Broward",
  "Palm Beach",
];

/**
 * Greater Los Angeles metro sub-areas (fixed list for Region filter when viewing the grouped metro).
 * Matches club.city after stripping optional ", CA" (see {@link getClubRegionForFilter}).
 */
export const GREATER_LA_REGIONS = [
  "Culver City",
  "Long Beach",
  "Los Angeles",
  "Santa Monica",
  "Signal Hill",
  "Venice",
];

/** Status values that make a meeting visible in the app (matches backend MEETING_STATUS_VISIBLE). */
const MEETING_VISIBLE_STATUSES = ["published", "upcoming", "completed"];

/**
 * Whether a meeting is visible in the app (counts as "published" for coverage/club completeness).
 * Matches backend: status in (published, upcoming, completed) or missing.
 * @param {{ status?: string } | null} meeting - Meeting/run object from API
 * @returns {boolean}
 */
export function isMeetingVisible(meeting) {
  if (!meeting) return false;
  const s = meeting.status;
  return s == null || s === "" || MEETING_VISIBLE_STATUSES.includes(s);
}

/**
 * Club completeness 0–100%: hero, about, goodToKnow, meetUp, address, + hasRun, + hasVerifiedRun.
 * @param {object} c - Club from API
 * @param {Array} runs - Meetings list for this city
 * @returns {number}
 */
export function clubCompleteness(c, runs) {
  if (!c) return 0;
  const runList = Array.isArray(runs) ? runs : [];
  const hasRun = runList.some((r) => r.club_id === c.id && isMeetingVisible(r));
  const hasVerifiedRun = runList.some(
    (r) =>
      r.club_id === c.id &&
      isMeetingVisible(r) &&
      r.address &&
      !String(r.address).toLowerCase().includes("check ig")
  );
  const hero = c.hero_image && String(c.hero_image).trim().length > 2;
  const about = c.description && String(c.description).trim().length > 2;
  const goodToKnow = c.home_base_description && String(c.home_base_description).trim().length > 2;
  const meetUp = c.home_base_address && String(c.home_base_address).trim().length > 2;
  const address = c.home_base_address && String(c.home_base_address).trim().length > 2;
  const filled =
    [hero, about, goodToKnow, meetUp, address].filter(Boolean).length +
    (hasRun ? 1 : 0) +
    (hasVerifiedRun ? 1 : 0);
  return Math.round((filled / 7) * 100);
}

/** Rolling window length; matches admin `range_key=next_7_days` (see console_stats_service._network_overview_date_range). */
export const CONSOLE_UPCOMING_RUN_WINDOW_DAYS = 7;

/**
 * Whether a run's scheduled_date falls in a rolling 7-day window [now, now+7d], inclusive.
 * Uses the browser's current time as `now` (local clock), so boundaries may differ slightly from
 * server UTC used by GET /admin/.../meetings?range_key=next_7_days and quality-checks recurring counts.
 * @param {object} run - Meeting-shaped object
 * @param {Date} [now] - Window start (default: current time)
 * @returns {boolean}
 */
export function isRunScheduledInNextSevenDays(run, now = new Date()) {
  if (!run?.scheduled_date) return false;
  const d = new Date(run.scheduled_date);
  if (Number.isNaN(d.getTime())) return false;
  const end = new Date(now.getTime() + CONSOLE_UPCOMING_RUN_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  return d >= now && d <= end;
}

/**
 * Rolling 7-day window for club cards / run list modals (browser-local "now"; matches {@link isRunScheduledInNextSevenDays}).
 * @param {Date} [now]
 * @returns {string}
 */
export function formatConsoleLocalRollingSevenDayRange(now = new Date()) {
  const end = new Date(now.getTime() + CONSOLE_UPCOMING_RUN_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  const dtf = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return `${dtf.format(now)} → ${dtf.format(end)} (this device's time)`;
}

/**
 * Recurring-runs onboarding window from quality checks API (UTC; same as admin `range_key=next_7_days`).
 * @param {string|null|undefined} startIso
 * @param {string|null|undefined} endIso
 * @returns {string|null}
 */
export function formatConsoleRecurringQualityWindowUtc(startIso, endIso) {
  if (typeof startIso !== "string" || typeof endIso !== "string" || !startIso.trim() || !endIso.trim()) {
    return null;
  }
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const dtf = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  });
  return `${dtf.format(start)} → ${dtf.format(end)} UTC`;
}

/**
 * Formatted recurring-runs window from {@link formatConsoleRecurringQualityWindowUtc}, or null.
 * @param {object|null|undefined} qualityData - Payload from console stats quality-checks API
 * @returns {string|null}
 */
export function getConsoleRecurringQualityRangeLabel(qualityData) {
  const w = qualityData?.onboarding_progress?.recurring_runs_window_utc;
  if (w == null || typeof w !== "object") return null;
  return formatConsoleRecurringQualityWindowUtc(w.start_utc, w.end_utc);
}

/**
 * Derived stats for club detail and club card: run counts, completeness, missing list.
 * @param {object} c - Club from API
 * @param {Array} runs - Meetings list
 * @param {string} [rangeKey] - Schedule window (matches URL `meetings_range`; default next_7_days)
 * @returns {object}
 */
export function getClubDetailStats(c, runs, rangeKey = CONSOLE_DEFAULT_RANGE) {
  if (!c?.id) {
    return {
      pct: 0,
      clubRuns: [],
      upcomingRunCount: 0,
      completedRunCount: 0,
      hasVerRun: false,
      compFields: [],
      fillColor: "var(--console-red)",
      missing: [],
      showMissing: false,
      missingLineColor: "var(--console-red)",
    };
  }
  const pct = clubCompleteness(c, runs);
  const runList = Array.isArray(runs) ? runs : [];
  const clubRuns = runList.filter((r) => r.club_id === c.id);
  const now = new Date();
  const rk = CONSOLE_RANGE_VALUE_SET.has(rangeKey) ? rangeKey : CONSOLE_DEFAULT_RANGE;
  const upcomingRunCount = clubRuns.filter((r) => isRunUpcomingInScheduleWindow(r, now, rk)).length;
  const completedRunCount = clubRuns.filter((r) => isRunCompletedInScheduleWindow(r, now, rk)).length;
  const hasVerRun = runList.some(
    (r) =>
      r.club_id === c.id &&
      isMeetingVisible(r) &&
      r.address &&
      !String(r.address).toLowerCase().includes("check ig")
  );
  const hasRun = runList.some((r) => r.club_id === c.id && isMeetingVisible(r));
  const heroOk = !!(c.hero_image && String(c.hero_image).trim().length > 2);
  const aboutOk = !!(c.description && String(c.description).trim().length > 2);
  const goodToKnowOk = !!(c.home_base_description && String(c.home_base_description).trim().length > 2);
  const meetUpOk = !!(c.home_base_address && String(c.home_base_address).trim().length > 2);
  const compFields = [
    { key: "hero", ok: heroOk },
    { key: "about", ok: aboutOk },
    { key: "goodToKnow", ok: goodToKnowOk },
    { key: "meetUp", ok: meetUpOk },
    { key: "run verified", ok: hasVerRun },
  ];
  const fillColor =
    pct === 100
      ? "var(--runcards-success)"
      : pct >= 70
        ? "var(--console-green)"
        : pct >= 40
          ? "var(--console-accent)"
          : "var(--console-red)";
  // Same criteria as clubCompleteness (7 slots; meet-up address counts twice in %).
  const missing = [];
  if (!heroOk) missing.push("hero image");
  if (!aboutOk) missing.push("about text");
  if (!goodToKnowOk) missing.push("good to know (meet-up notes)");
  if (!meetUpOk) missing.push("meet-up address");
  if (!hasRun) missing.push("at least one visible run (published / upcoming / completed)");
  else if (!hasVerRun) missing.push("run with a real meet address (not empty, not “check IG”)");
  const showMissing = missing.length > 0 && pct < 100;
  const missingLineColor = pct < 70 ? "var(--console-red)" : "var(--console-muted)";
  return {
    pct,
    clubRuns,
    upcomingRunCount,
    completedRunCount,
    hasVerRun,
    compFields,
    fillColor,
    missing,
    showMissing,
    missingLineColor,
  };
}

/**
 * City id or slug values that use South Florida sub-regions for filtering/grouping.
 */
const SOUTH_FLORIDA_CITY_IDS_OR_SLUGS = ["southflorida", "south-florida", "boca-raton"];

/** City param values that use Greater LA fixed sub-area regions (metro id + member slugs for deep links). */
const GREATER_LA_CITY_IDS_OR_SLUGS = [
  "greater-los-angeles",
  "greaterlosangeles",
  "culver-city",
  "long-beach",
  "los-angeles",
  "santa-monica",
  "signal-hill",
  "venice",
];

/**
 * Whether the current city uses the South Florida region list (fixed sub-regions).
 * @param {string} [cityId] - URL city param (id or slug)
 * @returns {boolean}
 */
export function isSouthFloridaCity(cityId) {
  if (!cityId || typeof cityId !== "string") return false;
  const normalized = cityId.trim().toLowerCase();
  return SOUTH_FLORIDA_CITY_IDS_OR_SLUGS.includes(normalized);
}

/**
 * Whether the current city uses the Greater Los Angeles region list (fixed sub-areas).
 * @param {string} [cityId] - URL city param (id or slug)
 * @returns {boolean}
 */
export function isGreaterLosAngelesCity(cityId) {
  if (!cityId || typeof cityId !== "string") return false;
  const normalized = cityId.trim().toLowerCase();
  return GREATER_LA_CITY_IDS_OR_SLUGS.includes(normalized);
}

/** Derive display name from city slug (e.g. "new-york" -> "New York"). */
export function slugToCityName(slug) {
  if (!slug || typeof slug !== "string") return "";
  return slug
    .trim()
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Map slot state from API to console badge class (planned | seeding | stable).
 * @param {string} state - "empty" | "fragile" | "stable" | "strong"
 * @returns {string} - console-badge--* class suffix
 */
export function getSlotBadgeClass(state) {
  if (!state || state === "empty") return "planned";
  if (state === "strong") return "stable";
  if (state === "fragile" || state === "stable") return "seeding";
  return "planned";
}

/**
 * Normalize API error to a single display string. Handles FastAPI/Pydantic detail (string or array).
 * @param {Error} error - Axios or other error with optional response.data.detail
 * @param {string} [fallback=""] - Used when detail is empty or missing
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback = "") {
  if (!error) return fallback;
  const detail = error.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length > 0) {
    const joined = detail.map((d) => d?.msg ?? d).filter(Boolean).join(" ").trim();
    return joined || fallback || (error.message && String(error.message)) || "";
  }
  if (detail != null && typeof detail !== "string") return String(detail);
  if (error.message && typeof error.message === "string") return error.message;
  return fallback;
}

// --- Feedback image (stored in DB as base64) ---

/** Allowed MIME types for feedback attachment images. */
export const FEEDBACK_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
/** Max file size for feedback image (2MB). */
export const FEEDBACK_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Check if a file is an allowed feedback image type.
 * @param {File | null} file
 * @returns {boolean}
 */
export function isFeedbackImageFile(file) {
  return file && file.type && FEEDBACK_IMAGE_TYPES.includes(file.type.toLowerCase());
}

/**
 * Read a file as base64 for feedback image (DB storage). Rejects if not an image or over size limit.
 * @param {File} file
 * @returns {Promise<{ base64: string, contentType: string }>}
 */
export function readFeedbackImageAsBase64(file) {
  if (!isFeedbackImageFile(file)) {
    return Promise.reject(new Error("Please choose a valid image (JPEG, PNG, GIF, or WebP)."));
  }
  if (file.size > FEEDBACK_IMAGE_MAX_BYTES) {
    return Promise.reject(new Error("Image must be 2MB or smaller."));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 =
        typeof dataUrl === "string" && dataUrl.includes(",") ? dataUrl.split(",")[1] : null;
      if (base64) resolve({ base64, contentType: file.type || "image/jpeg" });
      else reject(new Error("Could not read image"));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Build a data URL for displaying a feedback image (base64 or legacy URL).
 * @param {string} [base64] - Base64 string (no data URL prefix)
 * @param {string} [contentType] - e.g. image/jpeg, image/png
 * @returns {string} data URL or empty string if no base64
 */
export function toFeedbackImageDataUrl(base64, contentType = "image/jpeg") {
  return base64 ? `data:${contentType};base64,${base64}` : "";
}

/**
 * Region options for the console Region filter (Clubs and Runs tabs).
 * Metro-grouped cities use a fixed sub-region list; others use distinct club.region or club.city.
 * @param {string} [cityId] - URL city param
 * @param {Array<{ region?: string, city?: string }>} [clubs] - List of clubs for current city
 * @returns {string[]}
 */
export function getConsoleRegionOptions(cityId, clubs = []) {
  if (isSouthFloridaCity(cityId)) return [...SOUTH_FLORIDA_REGIONS];
  if (isGreaterLosAngelesCity(cityId)) return [...GREATER_LA_REGIONS];
  const set = new Set();
  clubs.forEach((c) => {
    const r = c.region || c.city;
    if (r) set.add(r);
  });
  return Array.from(set).sort();
}

/**
 * Region value to use when filtering by region (club or run's club).
 * @param {{ region?: string, city?: string } | null} club - Club object
 * @param {boolean} useSouthFloridaRegions - Whether the city uses the fixed South Florida list
 * @param {boolean} [useGreaterLaRegions] - Whether the city uses the fixed Greater LA list
 * @returns {string | undefined}
 */
export function getClubRegionForFilter(club, useSouthFloridaRegions, useGreaterLaRegions = false) {
  if (!club) return undefined;
  if (useSouthFloridaRegions) return club.region;
  if (useGreaterLaRegions) {
    const raw = String(club.region || club.city || "").trim();
    return raw.replace(/,\s*CA\s*$/i, "").trim() || undefined;
  }
  return club.region || club.city;
}

// --- Console image upload (same shape as mobile: url, order, is_primary) ---

/** Max carousel/run images (matches mobile MAX_IMAGES and backend MAX_CLUBCARD_IMAGES). */
export const MAX_CAROUSEL_IMAGES = 5;

/**
 * Trimmed URL string for duplicate checks (carousel / run images).
 * @param {unknown} url
 * @returns {string}
 */
export function normalizeImageUrlForDedupe(url) {
  return typeof url === "string" ? url.trim() : "";
}

/**
 * Dedupe http(s) image URLs; preserve first-seen order.
 * @param {unknown[]} urls
 * @returns {string[]}
 */
export function dedupeImageUrlsPreserveOrder(urls) {
  if (!Array.isArray(urls)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of urls) {
    const u = normalizeImageUrlForDedupe(raw);
    if (!u.startsWith("http") || seen.has(u)) continue;
    seen.add(u);
    out.push(u);
  }
  return out;
}

/**
 * Return a new array with index and index+delta swapped. No-op if out of bounds.
 * @template T
 * @param {T[]} arr
 * @param {number} index
 * @param {-1|1} delta
 * @returns {T[]}
 */
export function swapNeighbors(arr, index, delta) {
  if (!Array.isArray(arr) || arr.length < 2) return arr;
  const j = index + delta;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[index], next[j]] = [next[j], next[index]];
  return next;
}

/**
 * Ordered console carousel slots: `url` / `remote` (use as-is), `pasted_url` (rehost via uploadPastedUrl), plus `file` uploads.
 * @param {Array<{ kind?: string, url?: string, file?: File }>} slots
 * @param {(file: File, index: number) => Promise<{ url?: string } | null | undefined>} uploadFile
 * @param {{
 *   onUploadError?: (index: number, error: unknown) => void
 *   uploadPastedUrl?: (url: string, index: number) => Promise<{ url?: string } | null | undefined>
 * }} [opts]
 * @returns {Promise<string[]>}
 */
export async function collectUrlsFromCarouselSlots(slots, uploadFile, opts = {}) {
  if (!Array.isArray(slots) || typeof uploadFile !== "function") return [];
  const { onUploadError, uploadPastedUrl } = opts;
  const urls = [];
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (!slot) continue;
    if (slot.kind === "url" || slot.kind === "remote") {
      const u = slot.url != null ? String(slot.url).trim() : "";
      if (u.startsWith("http")) urls.push(u);
    } else if (slot.kind === "pasted_url") {
      const u = slot.url != null ? String(slot.url).trim() : "";
      if (!u.startsWith("http")) continue;
      if (typeof uploadPastedUrl === "function") {
        try {
          const res = await uploadPastedUrl(u, i);
          const out = res && typeof res.url === "string" ? res.url.trim() : "";
          if (out) urls.push(out);
        } catch (e) {
          onUploadError?.(i, e);
        }
      } else {
        urls.push(u);
      }
    } else if (slot.kind === "file" && slot.file) {
      try {
        const res = await uploadFile(slot.file, i);
        const url = res && typeof res.url === "string" ? res.url.trim() : "";
        if (url) urls.push(url);
      } catch (e) {
        onUploadError?.(i, e);
      }
    }
  }
  return urls;
}

/**
 * Build images array from URL strings. Same shape as mobile: { url, order, is_primary } (first is primary).
 * Duplicate URLs are dropped (first wins).
 * @param {string[]} urls - Image URLs
 * @returns {Array<{ url: string, order: number, is_primary: boolean }>}
 */
export function buildImagesArrayFromUrls(urls) {
  const uniq = dedupeImageUrlsPreserveOrder(urls);
  if (uniq.length === 0) return [];
  return uniq.map((url, index) => ({
    url,
    order: index,
    is_primary: index === 0,
  }));
}

/**
 * Normalize existing API images to merge with new uploads. Keeps only remote URLs.
 * @param {Array<{ url?: string }>} [existingImages] - clubcard_images or meeting images from API
 * @returns {Array<{ url: string, order: number, is_primary: boolean }>}
 */
export function normalizeExistingImages(existingImages) {
  if (!Array.isArray(existingImages) || existingImages.length === 0) return [];
  const withUrls = existingImages
    .filter((i) => i?.url && String(i.url).startsWith("http"))
    .map((i) => i.url);
  return buildImagesArrayFromUrls(withUrls);
}

/** Claim status display labels (internal, invited, claimed). Shared by ClubsView and ClubDetail. */
export const CLAIM_LABEL = { internal: "Internal", invited: "Invited", claimed: "Claimed" };

/** Anchor slots for "three-run rule" and expansion (Sat AM, Sun AM, Mon PM). */
export const ANCHOR_SLOTS = [
  { day: "Sat", slot: "am" },
  { day: "Sun", slot: "am" },
  { day: "Mon", slot: "pm" },
];

/** Search query ideas per slot for Expansion. {city} is replaced by city name. */
export const GAP_SEARCH_IDEAS = {
  "Mon AM": [
    "Monday morning run club {city}",
    "sunrise run {city} Monday",
    "Monday AM running crew {city}",
  ],
  "Mon PM": ["Monday night run club {city}", "weekly run Monday evening {city}"],
  "Tue AM": ["Tuesday track {city}", "Tuesday morning run club {city}"],
  "Tue PM": ["Tuesday night run {city}", "Tuesdays run club {city} instagram"],
  "Wed AM": [
    "Wednesday morning run {city}",
    "midweek run AM {city}",
    "Wednesday sunrise run {city}",
  ],
  "Wed PM": ["Wednesday social run {city}", "midweek miles {city}"],
  "Thu AM": ["Thursday AM run club {city}", "Thursday track {city}"],
  "Thu PM": ["Thursday night run {city}", "Thursday tempo {city}"],
  "Fri AM": ["Fast Fridays {city}", "Friday morning run club {city}"],
  "Fri PM": ["Friday social run {city}", "Friday night run club {city}"],
  "Sat AM": ["Saturday long run {city}", "Saturday morning run club {city}"],
  "Sat PM": ["Saturday afternoon run {city}"],
  "Sun AM": [
    "Sunday long run {city}",
    "Sunday shakeout {city}",
    "Sunday run club {city}",
  ],
  "Sun PM": ["Sunday evening run {city}", "Sunday social run {city}"],
};

/**
 * Map event_type (API) to runType for distribution views.
 * @param {string} [eventType] - API event_type value
 * @returns {string} - easy | long | workout | general | beginner
 */
export function eventTypeToRunType(eventType) {
  if (!eventType) return "general";
  const t = String(eventType).toLowerCase();
  if (t.includes("easy") || t.includes("social")) return "easy";
  if (t.includes("long")) return "long";
  if (
    t.includes("workout") ||
    t.includes("track") ||
    t.includes("speed") ||
    t.includes("tempo")
  )
    return "workout";
  if (t.includes("beginner")) return "beginner";
  return "general";
}

/**
 * True if the string can be parsed as an http(s) URL (prepends https:// when no scheme).
 * @param {string} [raw]
 * @returns {boolean}
 */
export function isProbablyHttpUrl(raw) {
  const s = (raw ?? "").trim();
  if (!s) return false;
  try {
    const href = /^https?:\/\//i.test(s) ? s : `https://${s}`;
    const u = new URL(href);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

